import React, { useState } from 'react';
import { Building2, Download, Upload, RefreshCw, AlertCircle } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { CHURCH_INFO } from '../../utils/constants';
import { storage, exportToCSV } from '../../utils/helpers';
import Button from '../common/Button';
import ChurchLogoSettings from './ChurchLogoSettings';

const ChurchSettingsTab = () => {
  const { data, resetData } = useData();
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleExport = () => {
    exportToCSV(data, `vomm_backup_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleReset = () => {
    if (showResetConfirm) {
      resetData();
      setShowResetConfirm(false);
      alert('Données réinitialisées avec succès !');
    } else {
      setShowResetConfirm(true);
    }
  };

  const stats = {
    users: data.users?.length || 0,
    members: data.members?.length || 0,
    donations: data.donations?.length || 0,
    expenses: data.expenses?.length || 0,
    fihirana: data.fihirana?.length || 0,
    programmes: data.programmes?.length || 0
  };

  return (
    <div className="space-y-6">
      {/* Informations de l'église */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center mb-4">
          <Building2 size={20} className="mr-2" />
          Informations de l'église
        </h3>
        
        <div className="space-y-3">
          <InfoRow label="Nom" value={CHURCH_INFO.name} />
          <InfoRow label="VOMM" value={CHURCH_INFO.fullName.vomm} />
          <InfoRow label="FJKM" value={CHURCH_INFO.fullName.fjkm} />
          <InfoRow label="Ville" value={CHURCH_INFO.city} />
          <InfoRow label="Pays" value={CHURCH_INFO.country} />
          <InfoRow 
            label="Adresse physique" 
            value={CHURCH_INFO.hasPhysicalAddress ? 'Oui' : 'Non'} 
          />
        </div>
      </div>

      {/* Logo de l'église */}
      <ChurchLogoSettings />

      {/* Structure du comité */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          Structure du Comité
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {CHURCH_INFO.committee.structure.map((position, index) => (
            <div 
              key={index}
              className="flex items-center space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
            >
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {index + 1}
              </div>
              <span className="text-gray-800 dark:text-white font-medium">{position}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Statistiques des données */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          Statistiques des données
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard label="Utilisateurs" value={stats.users} />
          <StatCard label="Membres" value={stats.members} />
          <StatCard label="Revenus" value={stats.donations} />
          <StatCard label="Dépenses" value={stats.expenses} />
          <StatCard label="Fihirana" value={stats.fihirana} />
          <StatCard label="Programmes" value={stats.programmes} />
        </div>
      </div>

      {/* Import / Export */}
      <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
          Gestion des données
        </h3>

        <div className="space-y-4">
          {/* Export */}
          <div className="flex items-start space-x-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <Download className="text-green-600 dark:text-green-400 flex-shrink-0 mt-1" size={24} />
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-1">
                Exporter les données
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Téléchargez toutes les données de l'application au format Excel (backup complet).
              </p>
              <Button
                onClick={handleExport}
                variant="success"
                icon={Download}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                Exporter en Excel
              </Button>
            </div>
          </div>

          {/* Import */}
          <div className="flex items-start space-x-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Upload className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" size={24} />
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-1">
                Importer des données
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Fonctionnalité à venir : Import de données depuis Excel.
              </p>
              <Button
                disabled
                variant="outline"
                icon={Upload}
                size="sm"
              >
                Bientôt disponible
              </Button>
            </div>
          </div>

          {/* Reset */}
          <div className="flex items-start space-x-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border-2 border-red-200 dark:border-red-800">
            <AlertCircle className="text-red-600 dark:text-red-400 flex-shrink-0 mt-1" size={24} />
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800 dark:text-white mb-1">
                Réinitialiser les données
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                <strong>⚠️ ATTENTION :</strong> Cette action supprimera toutes les données et restaurera les données initiales. Cette action est irréversible !
              </p>
              
              {showResetConfirm && (
                <div className="bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg p-3 mb-3">
                  <p className="text-sm text-red-800 dark:text-red-300 font-semibold mb-2">
                    Êtes-vous absolument sûr ?
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-400">
                    Toutes les données seront perdues. Assurez-vous d'avoir exporté vos données avant de continuer.
                  </p>
                </div>
              )}

              <div className="flex space-x-3">
                {showResetConfirm ? (
                  <>
                    <Button
                      onClick={() => setShowResetConfirm(false)}
                      variant="outline"
                      size="sm"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={handleReset}
                      variant="danger"
                      icon={RefreshCw}
                      size="sm"
                    >
                      Confirmer la réinitialisation
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleReset}
                    variant="outline"
                    icon={RefreshCw}
                    size="sm"
                    className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    Réinitialiser
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Informations système */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-xs text-gray-600 dark:text-gray-400">
        <p><strong>Version :</strong> 2.0.0</p>
        <p><strong>Stockage :</strong> localStorage (navigateur)</p>
        <p><strong>Dernière mise à jour :</strong> {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-600">
    <span className="text-gray-600 dark:text-gray-400">{label}</span>
    <span className="font-medium text-gray-800 dark:text-white">{value}</span>
  </div>
);

const StatCard = ({ label, value }) => (
  <div className="bg-gray-50 dark:bg-gray-600 rounded-lg p-4 text-center">
    <p className="text-2xl font-bold text-gray-800 dark:text-white">{value}</p>
    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{label}</p>
  </div>
);

export default ChurchSettingsTab;
